// third party imports
import { useState, useEffect, useContext, useMemo, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// internal rebet imports
import './stylesLogIn.css'
import { NAVIGATION_ROUTES } from '../../utils/NavigationUtils';
import PublicAdminPanelInstance from '../../api/AffiliatePanelInstance';
import { API_ROUTES } from '../../api/ApiRoutes';
import AppContext from '../../context/AppContext';
import { AppStrings } from '../../utils/constants/AppStrings';
import {
  AUTH_FLOW_ACTIONS,
  AUTH_FLOW_STEPS,
  AUTH_RESPONSE_TYPES,
  authFlowReducer,
  classifyAuthResponse,
  createInitialAuthFlowState,
} from '../../utils/auth/authFlow';
import { finalizeAuthenticatedSession } from '../../services/authSessionService';
import {
  associatePamMfa,
  challengePamMfa,
  verifyPamMfaEnrollment,
} from '../../queries/auth/pamMfaClient';
import {
  buildPamTotpUri,
  getPamMfaErrorMessage,
} from '../../utils/auth/pamMfa';

// component imports
import RebetLoader from '../CommonComponents/RebetLoader/RebetLoader';
import AuthenticatorEnrollment from './TwoFactor/AuthenticatorEnrollment';
import AuthenticatorChallenge from './TwoFactor/AuthenticatorChallenge';

// asset imports
import { BRAND_ICONS_URLS } from '../../utils/Constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  getStoredGuestLanguage,
  resolvePlatformDefaultLanguage,
  setStoredGuestLanguage,
} from '../../utils/i18n/languagePreference';
import { canonicalizeAvailableLanguages } from '../../utils/i18n/availableLanguages';
import { canonicalizeLanguageTag } from '../../utils/i18n/supportedLanguages';
import { LanguageSelect } from '../shared';
import { useIpWhitelistAccess } from '../IpWhitelistGate/IpWhitelistGate';
import { isIpWhitelistBlockedError } from '../../services/ipWhitelistAccessService';
import {
  AFFILIATE_PORTAL_SET_PASSWORD_ROUTE,
} from '../../routing/affiliatePortalRoutes';
import {
  AUTH_AUDIENCES,
  AUTH_AUDIENCE_VALUES,
} from '../../utils/auth/authAudience';
import { postPortalSignIn } from '../../queries/affiliate-portal/usePortalSignIn';
import { REGULATOR_PORTAL_SET_PASSWORD_ROUTE } from '../RegulatorPortal/regulatorPortalRoutes';


const LogIn = ({ audience = AUTH_AUDIENCES.STAFF }) => {
  const dispatch = useDispatch();
  // non-state variables
  const navigate = useNavigate();
  const { setToken, setLastLogInTimestamp } = useContext(AppContext) || {}
  const { blockAccess } = useIpWhitelistAccess();

  // Pre-auth language switching. Options and default come from the
  // unauthenticated config's `localization` block. The guest key wins over
  // the profile preference at login (useAdminAuth PATCHes it) and survives
  // logout, so the login screen keeps the choice.
  const { appConfig } = useSelector((state) => state.config);
  const guestLanguage =
    canonicalizeLanguageTag(getStoredGuestLanguage()) ??
    resolvePlatformDefaultLanguage(appConfig);
  const availableLanguages = useMemo(() => {
    const options = canonicalizeAvailableLanguages(
      appConfig?.localization?.available_languages,
    );
    // Keep the active language listed (as Header does) so the select can
    // render its own value when availability no longer covers it.
    return options.includes(guestLanguage)
      ? options
      : [guestLanguage, ...options];
  }, [appConfig, guestLanguage]);

  // antd Select passes the selected value directly (not a DOM event).
  const handleGuestLanguageChange = (lng) => {
    if (!lng || lng === guestLanguage) return;
    setStoredGuestLanguage(lng);
    // Apply mechanism v1: full reload re-keys i18next + AppStrings from the
    // stored guest choice (see the boot keying in src/index.js).
    window.location.reload();
  };

  // state variables
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isApiRunning, setIsApiRunning] = useState(false)
  const [loginButtonActive, setLoginButtonActive] = useState(false)
  const [lastApiCallError, setLastApiCallError] = useState(false)
  const [lastApiErrorMessage, setLastApiErrorMessage] = useState("")
  const [authFlowState, dispatchAuthFlow] = useReducer(
    authFlowReducer,
    undefined,
    createInitialAuthFlowState,
  );

  // use effects'
  useEffect(() => {
    if (email && password) {
      setLoginButtonActive(true)
    } else {
      setLoginButtonActive(false)
    }
  }, [email, password])

  useEffect(() => {
    if (
      authFlowState.step !== AUTH_FLOW_STEPS.TOTP_ENROLLMENT ||
      !authFlowState.expiresIn
    ) {
      return undefined;
    }

    const expirationTimer = window.setTimeout(() => {
      dispatchAuthFlow({ type: AUTH_FLOW_ACTIONS.RESET });
      setPassword("");
      setLastApiCallError(true);
      setLastApiErrorMessage(
        AppStrings.PAM_2FA_ENROLLMENT_TICKET_EXPIRED,
      );
    }, authFlowState.expiresIn * 1000);

    return () => window.clearTimeout(expirationTimer);
  }, [authFlowState.expiresIn, authFlowState.step]);

  // Staff landing-page selection remains owned by RedirectIfAuthenticated.
  // External portal sessions navigate directly during finalization.

  // api functions

  // helper functions
  const handleEmailChange = (event) => {
    setEmail(event?.target?.value)
  }

  const handlePasswordChange = (event) => {
    setPassword(event?.target?.value)
  }

  const finalizeSession = async (token) => {
    await finalizeAuthenticatedSession({
      token,
      email,
      setToken,
      setLastLogInTimestamp,
      dispatch,
      navigate,
      onIpBlocked: blockAccess,
      audience,
    });
  };

  const beginTotpEnrollment = async ({
    enrollmentTicket,
    expiresIn,
    username,
  }) => {
    const response = await associatePamMfa(enrollmentTicket);
    const secret = response?.data?.secret_code;
    const associatedUsername = response?.data?.username;
    if (
      typeof secret !== "string" ||
      !secret ||
      typeof associatedUsername !== "string" ||
      !associatedUsername
    ) {
      throw new Error("Malformed MFA association response");
    }

    dispatchAuthFlow({
      type: AUTH_FLOW_ACTIONS.REQUIRE_TOTP_ENROLLMENT,
      username,
      enrollmentTicket,
      expiresIn,
      secret,
      otpauthUri: buildPamTotpUri({
        issuer: AppStrings.PAM_APP_NAME,
        username: associatedUsername,
        secret,
      }),
    });
  };

  const handleLoginPressed = async () => {

    setIsApiRunning(true)
    setLastApiCallError(false)
    setLastApiErrorMessage("")

    const params = {
      user_name: email, 
      password: password
    }

    try {

      const responseData =
        audience === AUTH_AUDIENCES.AFFILIATE
          ? await postPortalSignIn(params)
          : (await PublicAdminPanelInstance?.post(API_ROUTES.LOG_IN, params))
              ?.data?.data;
      const classifiedResponse = classifyAuthResponse(responseData);

      if (
        classifiedResponse.type ===
        AUTH_RESPONSE_TYPES.NEW_PASSWORD_REQUIRED
      ) {
        dispatchAuthFlow({
          type: AUTH_FLOW_ACTIONS.REQUIRE_NEW_PASSWORD,
          username: email,
          sessionToken: classifiedResponse.sessionToken,
        });
        let setPasswordRoute = NAVIGATION_ROUTES?.SET_PASSWORD;
        if (audience === AUTH_AUDIENCES.AFFILIATE) {
          setPasswordRoute = AFFILIATE_PORTAL_SET_PASSWORD_ROUTE;
        } else if (audience === AUTH_AUDIENCES.REGULATOR) {
          setPasswordRoute = REGULATOR_PORTAL_SET_PASSWORD_ROUTE;
        }
        navigate(setPasswordRoute, {
          state: {
            username: email,
            sessionToken: classifiedResponse.sessionToken,
            audience,
          },
        });
        return;
      }

      if (
        classifiedResponse.type ===
          AUTH_RESPONSE_TYPES.TOTP_ENROLLMENT_REQUIRED
      ) {
        if (audience !== AUTH_AUDIENCES.STAFF) {
          throw new Error("MFA enrollment is only supported for PAM staff");
        }
        await beginTotpEnrollment({
          enrollmentTicket: classifiedResponse.enrollmentTicket,
          expiresIn: classifiedResponse.expiresIn,
          username: email,
        });
        return;
      }

      if (
        classifiedResponse.type ===
        AUTH_RESPONSE_TYPES.TOTP_CHALLENGE_REQUIRED
      ) {
        if (audience !== AUTH_AUDIENCES.STAFF) {
          throw new Error("MFA challenge is only supported for PAM staff");
        }
        dispatchAuthFlow({
          type: AUTH_FLOW_ACTIONS.REQUIRE_TOTP_CHALLENGE,
          username: email,
          sessionToken: classifiedResponse.sessionToken,
        });
        setPassword("");
        return;
      }

      if (classifiedResponse.type !== AUTH_RESPONSE_TYPES.FINAL_TOKENS) {
        setLastApiCallError(true);
        setLastApiErrorMessage(AppStrings.PAM_GENERIC_REQUEST_FAILED);
        return;
      }

      await finalizeSession(classifiedResponse.token);

    } catch (error) {
      if (
        audience === AUTH_AUDIENCES.STAFF &&
        isIpWhitelistBlockedError(error)
      ) {
        blockAccess();
        return;
      }
      console.error("Error logging in", error)
      setLastApiCallError(true)
      setLastApiErrorMessage(getPamMfaErrorMessage(error))
    } finally {
      setIsApiRunning(false)
    }

  }

  const handleEnrollmentVerification = async (code) => {
    const response = await verifyPamMfaEnrollment({
      enrollmentTicket: authFlowState.enrollmentTicket,
      code,
    });
    const classifiedResponse = classifyAuthResponse(response?.data);
    if (classifiedResponse.type !== AUTH_RESPONSE_TYPES.FINAL_TOKENS) {
      throw new Error("Malformed final authentication response");
    }
    dispatchAuthFlow({ type: AUTH_FLOW_ACTIONS.RESET });
    await finalizeSession(classifiedResponse.token);
  };

  const handleChallengeVerification = async (code) => {
    const response = await challengePamMfa({
      username: authFlowState.username,
      session: authFlowState.sessionToken,
      code,
    });
    const classifiedResponse = classifyAuthResponse(response?.data);
    if (classifiedResponse.type !== AUTH_RESPONSE_TYPES.FINAL_TOKENS) {
      throw new Error("Malformed final authentication response");
    }
    dispatchAuthFlow({ type: AUTH_FLOW_ACTIONS.RESET });
    await finalizeSession(classifiedResponse.token);
  };

  const handleTotpCancel = () => {
    dispatchAuthFlow({ type: AUTH_FLOW_ACTIONS.RESET });
    setPassword("");
    setLastApiCallError(false);
    setLastApiErrorMessage("");
  };

  const handleTransientAuthExpired = (message) => {
    dispatchAuthFlow({ type: AUTH_FLOW_ACTIONS.RESET });
    setPassword("");
    setLastApiCallError(true);
    setLastApiErrorMessage(message);
  };
 

  return (
    <div
      className={
        authFlowState.step === AUTH_FLOW_STEPS.TOTP_ENROLLMENT ||
        authFlowState.step === AUTH_FLOW_STEPS.TOTP_CHALLENGE
          ? "container container-authenticator-enrollment"
          : "container"
      }
    >
      <div className="brandingColumn">
        <div className="brandingColumnContent">
          <img src={process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL + BRAND_ICONS_URLS.companylogo} alt='logo' className="brandingIconOnly" />
          <span className="brandingIconOnlyText">
            {AppStrings.PAM_APP_NAME}
          </span>
        </div>
      </div>
  
      <div className="formColumn">
        <div className="formContentContainer">
          <div className="login-language-row">
            <LanguageSelect
              id="login-language-select"
              className="login-language-select"
              data-testid="login-language-select"
              value={guestLanguage}
              options={availableLanguages}
              onChange={handleGuestLanguageChange}
            />
          </div>

          {authFlowState.step === AUTH_FLOW_STEPS.TOTP_ENROLLMENT ? (
            <AuthenticatorEnrollment
              secret={authFlowState.secret}
              otpauthUri={authFlowState.otpauthUri}
              onVerify={handleEnrollmentVerification}
              onCancel={handleTotpCancel}
              onExpired={handleTransientAuthExpired}
            />
          ) : authFlowState.step === AUTH_FLOW_STEPS.TOTP_CHALLENGE ? (
            <AuthenticatorChallenge
              onVerify={handleChallengeVerification}
              onCancel={handleTotpCancel}
              onSessionExpired={handleTransientAuthExpired}
            />
          ) : (
          <form
            autoComplete="on"
            aria-label={AppStrings.ADMIN_LOG_IN}
            onSubmit={(event) => {
              event.preventDefault();
              handleLoginPressed();
            }}
          >
            <p className="loginHeaderText">
              {AppStrings.ADMIN_LOG_IN}
            </p>
    
            <label className="fieldLabelText" htmlFor="login-email">
              {AppStrings.EMAIL}
            </label>
    
            <div className="fieldInputFrame">
              <input
                id="login-email"
                name="username"
                type="text"
                autoComplete="username"
                placeholder=""
                className="fieldInput"
                value={email}
                onChange={handleEmailChange}
                data-testid="login-email"
              />
            </div>
    
            <label className="fieldLabelText" htmlFor="login-password">
              {AppStrings.PASSWORD}
            </label>
    
            <div className="fieldInputFrame">
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder=""
                className="fieldInput"
                value={password}
                onChange={handlePasswordChange}
                data-testid="login-password"
              />
            </div>
    
            <button
              type="submit"
              className={
                isApiRunning ||
                !loginButtonActive ||
                authFlowState.step !== AUTH_FLOW_STEPS.PASSWORD
                ? "loginButton-sending" 
                : "loginButton"
              }
            > 
              {
                isApiRunning
                ? (
                  <RebetLoader 
                    height={'50px'}
                    width={'50px'}
                  />
                )
                : (
                  <p className={loginButtonActive ? "loginButtonText" : "loginButtonText-inactive"}>
                      {AppStrings.LOG_IN}
                  </p>
                )
              }
            </button>

            <div className="login-error-message-container">
            {
              lastApiCallError 
                ? (
                  <div className='error-message'>
                    {lastApiErrorMessage}
                  </div>
                ) 
                : null
              }
            </div>
          </form>
          )}

        </div>
      </div>
    </div>
  );
};

LogIn.propTypes = {
  audience: PropTypes.oneOf(AUTH_AUDIENCE_VALUES),
};

export default LogIn;
